import React, { useEffect, useState } from "react";
import { Table } from "reactstrap";
import _ from "lodash";
import "./mui-table.scss";
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Header, MuiTableProps, SelectValue } from "./mui-table-props";
import RenderCellContent from "./render-cell-content";
import DropdownMenu from "../menu-component/menu-component";
import AscIcon from "../../../assets/icons/sort-asc.svg";
import DescIcon from "../../../assets/icons/sort-desc.svg";
import UnsortIcon from "../../../assets/icons/un-sort.svg";
import CountryCodeWithPhoneNumber from "../phone-number-input-component/country-code-phone-numner-input";
import icons from "../../../assets/icons/icons";
import ActionsMenu from "./actions-menu";
import { IconButton } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "../../../assets/icons/edit-icon.svg";
import RunIcon from "../../../assets/icons/runIcon.svg";
import DeleteIcon from "../../../assets/icons/delete-grey-icon.svg";
import { ActionOption } from "./mui-table-props";
import { useLogin } from "../../hooks/useUser";
import CustomTooltip from "../custom-tooltip";
import { useCheckIsAFPSuperAdmin } from "../../../utils/checkIsAFPSuperAdmin";
import { Permissions } from "../../../features/leads/lead-view/lead-view.props";
import CustomTooltipWithAlignment from "../custom-tooltip";

const sortOptions = [
  {
    value: "Asc",
    label: "Asc",
    icon: <img src={AscIcon} alt="asc" className="sort-icon" />,
  },
  {
    value: "Desc",
    label: "Desc",
    icon: <img src={DescIcon} alt="desc" className="sort-icon" />,
  },
];
const unsortValue = {
  value: "Unsort",
  label: "Unsort",
  icon: <img src={UnsortIcon} alt="unsort" className="sort-icon" />,
};

const getActionTypes = (
  row: any,
  runClickedIds: Set<number>,
  checkIsAFPSuperAdmin: () => boolean,
  permissions?: Permissions
): ActionOption[] => {
  const isSuperAdmin = checkIsAFPSuperAdmin();

  const actions: ActionOption[] = [
    {
      label: "Edit",
      value: "edit",
      id: 1,
      src: EditIcon,
      disabled: !isSuperAdmin || !permissions?.canWrite,
      tooltipText: !isSuperAdmin ? "Only AFP Super Admins can edit rule" : "",
    },
    {
      label: "Run",
      value: "run",
      id: 2,
      src: RunIcon,
      disabled:
        !isSuperAdmin ||
        row?.disableRun ||
        runClickedIds?.has(row?.id) ||
        !permissions?.canWrite ||
        row?.LeadAssignmentTracker?.length > 0,
      tooltipText: row?.disableRun
        ? "You can't run Inactive rule"
        : !isSuperAdmin
          ? "Only AFP Super Admin can run rule"
          : runClickedIds?.has(row?.id) ||
              row?.LeadAssignmentTracker?.length > 0
            ? "Rule execution is in progress. You can run it again once the execution is completed."
            : "",
    },
    {
      label: "Archive",
      value: "archive",
      id: 3,
      src: DeleteIcon,
      disabled: !isSuperAdmin || row?.leadCount > 0 || !permissions?.canDelete,
      tooltipText:
        row?.leadCount > 0
          ? "You can't archive rule with assigned leads count greater than 0"
          : !isSuperAdmin
            ? "Only AFP Super Admin can archive rule"
            : undefined,
    },
  ];

  return actions;
};

const selectTableColumn = (
  row: any,
  header: any,
  onSelectChange: any,
  rowIndex: any,
  selectColumns: any,
  onHandleChange: any,
  onColumnClick?: any
) => {
  let options = selectColumns?.[header?.key] || false;
  const value = header?.valueGetter
    ? header?.valueGetter(row)
    : _.get(row, header.key, null);

  const getColor = (optionValue: string | null) => {
    switch (optionValue) {
      case "ACTIVE":
        return "green";
      case "INACTIVE":
        return "red";
      default:
        return "inherit";
    }
  };

  const handlePhoneInput = (value: string, country: any) => {
    const countryCode = country.dialCode;
    if (!countryCode) {
      console.warn("Missing dialCode in country object");
      return;
    }
    const numberOnly = value.slice(countryCode.length + 1);
    const formatted: any = `+${countryCode} ${numberOnly}`;

    onHandleChange(rowIndex, header?.key, formatted);
  };

  if (options?.length && header?.handleOptions) {
    options = header?.handleOptions(row, options);
  }

  return (
    <>
      {options?.length ? (
        <Box sx={{ height: "20px", minHeight: "20px" }}>
          <Select
            value={value}
            onChange={(event: SelectChangeEvent<string>) =>
              onSelectChange(rowIndex, header?.key, event.target.value)
            }
            displayEmpty={true}
            IconComponent={ExpandMoreIcon}
            className={`Mui-table-select-dropdown ${!value ? "Mui-table-select-disable-value" : ""}`}
            disabled={header?.isDisabled(row) || false}
            renderValue={(selected) => {
              const selectedOption = options.find(
                (option: any) => option.value === selected
              );
              return (
                <span style={{ color: getColor(selected) }}>
                  {selectedOption
                    ? selectedOption.label
                    : `Select ${header?.label}`}
                </span>
              );
            }}
            fullWidth
            size="small"
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  maxWidth: 200,
                  overflow: "auto",
                },
              },
            }}
          >
            {header.key !== "status" && (
              <MenuItem disabled value="">
                Select {header?.label}
              </MenuItem>
            )}
            {options.map((option: any) => (
              <MenuItem
                key={option.value}
                value={option.value}
                style={{
                  color: getColor(option.value),
                }}
                disabled={option?.disabled || false}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : (
        <>
          {header?.fieldName === "phoneNumber" ? (
            <>
              <CountryCodeWithPhoneNumber
                value={value}
                fieldName={header.fieldName}
                placeholder="Phone"
                onChange={handlePhoneInput}
                disabled={header?.isDisabled(row) || false}
                key={rowIndex}
                defaultCountry="us"
                showExampleFormat={header?.showPhoneExampleFormat ?? true}
              />
            </>
          ) : (
            <>
              {" "}
              <TextField
                type={header?.fieldType}
                name={header?.fieldName}
                value={value || ""}
                placeholder={header?.label}
                disabled={header?.isDisabled(row) || false}
                onChange={(event) => {
                  const onChangevalue = event.target.value;

                  if (header?.fieldType === "email") {
                    const sanitizedValue = onChangevalue.replace(
                      /[^A-Za-z0-9._%+-@]/g,
                      ""
                    );
                    const [localPart, domainPart] = sanitizedValue.split("@");

                    if (domainPart !== undefined) {
                      if (domainPart.includes("..")) {
                        event.target.value = `${localPart}@${domainPart.replace(/\.+/g, ".")}`;
                      } else if (domainPart.split(".").length > 2) {
                        const parts = domainPart.split(".");
                        const extension = parts.pop();
                        const domain = parts.join(".");
                        event.target.value = `${localPart}@${domain}.${extension?.slice(0, 6)}`;
                      } else {
                        event.target.value = sanitizedValue;
                      }
                    } else {
                      event.target.value = sanitizedValue;
                    }
                  } else if (header?.isName) {
                    event.target.value = onChangevalue.replace(
                      /[^A-Za-z -]/g,
                      ""
                    );
                  } else if (header?.isPhonenumber) {
                    event.target.value = onChangevalue.replace(/[^\d+ ]/g, "");
                  } else if (header?.isAlphaNumeric) {
                    event.target.value = onChangevalue.replace(
                      /[^A-Za-z0-9 ]/g,
                      ""
                    );
                  }
                  onHandleChange(rowIndex, header?.key, event.target.value);
                }}
                className="Mui-table-custom-textfield"
                inputProps={{ maxlength: header?.maxLength }}
              />
            </>
          )}
        </>
      )}
    </>
  );
};
const MUITable = <T extends Record<string, any>>({
  headers,
  data,
  isStriped = true,
  selectDropDownOptions = {},
  onSelectChange,
  onSort,
  onHandleChange,
  noResultsTxt,
  onColumnClick,
  isShowPaymentNote = false,
  onRowClick,
  handleAction,
  runClickedIds,
  setRunClickedIds,
  fieldName,
  permissions,
  rowClassName,
  actionOptions = [],
  isShowUserCredentialEmailOption = false,
  resetSort = false,
  rowWise = false,
}: MuiTableProps<T>) => {
  const [selected, setSelected] = useState<SelectValue>({
    value: null,
    activeHeader: null,
  });

  // Reset sort state when resetSort prop changes
  useEffect(() => {
    if (resetSort) {
      setSelected({
        value: null,
        activeHeader: null,
      });
    }
  }, [resetSort]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const { tenantDetails, userDetails } = useLogin();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleMenuOptionClick = (option: ActionOption) => {
    if (option?.label === "Run" && menuRow?.id != null) {
      if (setRunClickedIds)
        setRunClickedIds((prev: any) => new Set(prev)?.add(menuRow.id));
    }

    if (handleAction) {
      handleAction(option?.label, menuRow?.id);
    }
    handleClose();
  };

  const handleSortOptions = (header: any) => {
    if (!sortOptions) return [];

    const isActiveHeader = header?.key === selected?.activeHeader?.key;
    const filteredOptions = sortOptions.filter(
      (option) => option.value !== selected?.value || !isActiveHeader
    );

    if (selected?.value && selected.value !== "Unsort" && isActiveHeader) {
      filteredOptions.push(unsortValue);
    }

    return filteredOptions;
  };

  const showSortText =
    selected?.activeHeader?.label && selected?.value !== "Unsort";

  const handleSort = (header: Header, select: string) => {
    if (onSort) {
      onSort(header.key, select);
      setSelected({
        activeHeader: header,
        value: select,
      });
    }
  };
  const getTextContent = (value: any): string | React.ReactNode => {
    if (!value) return "-";

    if (typeof value === "string") {
      return (
        <span
          className={`table-cell-content 
        ${
          fieldName === "assignment-rules"
            ? value === "Active"
              ? "active-color"
              : value === "Inactive"
                ? "inActive-color"
                : ""
            : ""
        }`}
          title={value}
        >
          {value}
        </span>
      );
    }

    if (React.isValidElement(value)) {
      const { props }: any = value;
      if (props.status || typeof value.type === "function") {
        return (
          <span
            className="table-cell-content clickable"
            title={props.status || props.children}
          >
            {value}
          </span>
        );
      }

      // For clickable React elements
      if (
        (props.onClick || typeof value.type === "function") &&
        !props.status
      ) {
        return (
          <span
            className="table-cell-content clickable"
            title={props.children || "Click"}
          >
            {value}
          </span>
        );
      }

      // Handle mixed children (text + React elements)
      if (Array.isArray(props.children)) {
        return (
          <span
            className="table-cell-content"
            title={props.children
              .map((child: any) => (typeof child === "string" ? child : ""))
              .join(" ")}
          >
            {props.children.map((child: any, index: number) =>
              typeof child === "string" ? child : child
            )}
          </span>
        );
      }

      //To handle Link
      if (
        React.isValidElement(value) &&
        typeof value.type === "object" &&
        value.type !== null &&
        "$$typeof" in value.type &&
        (value.type as any).$$typeof.toString() ===
          "Symbol(react.forward_ref)" &&
        ((value.type as any).render?.name === "LinkWithRef" ||
          (value.type as any).render?.displayName === "Link")
      ) {
        const getLinkText = (node: React.ReactNode): string | number => {
          if (typeof node === "string" || typeof node === "number") return node;
          if (Array?.isArray(node)) return node?.map(getLinkText)?.join(" ");
          if (React?.isValidElement(node)) {
            const element = node as React.ReactElement<any>;
            return getLinkText(element?.props?.children);
          }
          return "";
        };

        const element = value as React.ReactElement<any>;
        const linkText = getLinkText(element.props.children);
        return (
          <span
            className="table-cell-content clickable"
            title={linkText.toString()}
          >
            {value}
          </span>
        );
      }

      //To handle Span

      if (React.isValidElement(value) && value.type === "span") {
        const element = value as React.ReactElement<any>;
        const className = element.props?.className || "";
        const children = element.props?.children;

        if (className.includes("leads-link") && typeof children === "string") {
          return (
            <span
              className="table-cell-content clickable"
              title={children.toString()}
            >
              {value}
            </span>
          );
        }
      }

      return value;
    }

    if (typeof value === "number") {
      return (
        <span className="table-cell-content" title={value.toString()}>
          {value}
        </span>
      );
    }

    return "N/A";
  };

  const handleOnclick = (row: any, header: Header) => {
    if (header?.isOnClick && onColumnClick) {
      onColumnClick(row);
    }
  };

  return (
    <>
      {" "}
      <Box className="filter-options-wrapper">
        <Box>
          {showSortText && (
            <Typography
              className="sort-text-wrapper"
              sx={{ fontSize: "14px", marginBottom: "5px" }}
            >
              Sort By{" "}
              <span>
                {selected?.activeHeader?.label} ({selected?.value})
              </span>{" "}
              -{" "}
              <span
                className="unsort-text"
                onClick={() => {
                  if (onSort) {
                    onSort("", "Unsort");
                    setSelected({
                      activeHeader: null,
                      value: "Unsort",
                    });
                  }
                }}
              >
                Unsort
              </span>
            </Typography>
          )}
        </Box>

        <Box>
          {isShowPaymentNote && (
            <Box className="partner-resend-email-wrapper">
              <Box className="payamnet-pending-wrapper">
                <Box className="payment-dot" />
                <span>Payment Pending</span>
              </Box>
              <Box className="email-resend-wrapper">
                <img src={icons.EmailResendIcon} alt="resent-email" />
                <span>Resend Link Option</span>
              </Box>
            </Box>
          )}
          {isShowUserCredentialEmailOption && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                marginRight: "22px",
                fontSize: "14px",
                fontFamily: 'Roboto, "sans-serif"',
                fontWeight: 400,
              }}
            >
              <img src={icons.EmailResendIcon} alt="resent-email" />
              <span>Resend User Credentials Option</span>
            </div>
          )}
        </Box>
      </Box>
      <div className="mui-table">
        <div className="table-container">
          <Table striped={isStriped} className="custom-table-wraper">
            <thead>
              <tr>
                {headers.map((header: any, index: number) => (
                  <th
                    key={header.key}
                    style={{ width: header?.fixedColumnWidth ?? "" }}
                  >
                    <div className="header-cell">
                      <div className="header-text">
                        {header.label === "Action" ? (
                          <span>{header.label}</span>
                        ) : (
                          header.label
                        )}
                      </div>
                      {header?.isSortable && (
                        <CustomTooltipWithAlignment
                          title="Sort"
                          placement="bottom"
                          offsetX={18}
                          arrow={false}
                        >
                        <span style={{ marginLeft: "35px" }}>
                          <DropdownMenu
                            options={handleSortOptions(header)}
                            onSelect={(select: string) => {
                              handleSort(header, select);
                            }}
                          />
                        </span>
                        </CustomTooltipWithAlignment>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data.map((row: any, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className={`${row?.isGracePeriod ? rowClassName : ""}  ${rowWise ? "" :(onRowClick ? "clickable-row" : "")}`}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {headers.map((header: Header, colIndex: number) => (
                      <td
                        key={colIndex}
                        onClick={(e) => {
                          if (row?.disableClick && colIndex === 0) {
                            e.stopPropagation();
                          }
                          if (
                            fieldName === "emailTemplates" &&
                            colIndex === 5
                          ) {
                            e.stopPropagation();
                          }
                          if (fieldName === "afp-support-tickets" &&
                            (colIndex === 0 || ((row['status'] !== "Closed") && colIndex === 4))) {
                            e.stopPropagation();
                          }
                          handleOnclick(row, header);
                        }}
                        style={{
                          cursor:
                            row?.disableClick && colIndex === 0
                              ? "not-allowed"
                              : row?.disableClick && rowWise === true ? 'default'
                              : (onRowClick || header?.isOnClick) ? "pointer" : "default"
                        }}
                        className={`${header?.classNameGetter && header?.classNameGetter(row, data, rowIndex)}`}
                      >
                        {header.key === "action" &&
                        row[header.key] === "verticalEllipse" ? (
                          <>
                            {row?.LeadAssignmentTracker?.length >= 0 &&
                            runClickedIds?.has(row?.id) ? (
                              <CustomTooltip
                                title="Rule execution is in progress. You can run it again once the execution is completed."
                                placement="right"
                                offsetX={18}
                              >
                                <IconButton
                                  onClick={(e) => handleMenuClick(e, row)}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </CustomTooltip>
                            ) : (
                              <IconButton
                                onClick={(e) => handleMenuClick(e, row)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            )}
                          </>
                        ) : header?.isEditable ? (
                          selectTableColumn(
                            row,
                            header,
                            onSelectChange,
                            rowIndex,
                            selectDropDownOptions,
                            onHandleChange
                          )
                        ) : (
                          <span
                            className={`${header?.isOnClick ? "table-cell-pointer" : ""}`}
                          >
                            <RenderCellContent
                              text={
                                header?.valueGetter
                                  ? header?.valueGetter(row)
                                  : getTextContent(row[header?.key])
                              }
                            />
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="no-data-row">
                  <td colSpan={headers?.length}>
                    <Box sx={{ textAlign: "center" }}>
                      {noResultsTxt || "No data found"}
                    </Box>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <ActionsMenu
            anchorEl={anchorEl}
            onClose={handleClose}
            onOptionClick={handleMenuOptionClick}
            options={
              fieldName === "assignment-rules"
                ? getActionTypes(
                    menuRow,
                    runClickedIds,
                    useCheckIsAFPSuperAdmin,
                    permissions
                  )
                : actionOptions?.length ? actionOptions : menuRow?.actionOptions || []
            }
          />
        </div>
      </div>
    </>
  );
};

export default MUITable;
